import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { VisitorEntities } from 'client/data/models/visitor';
import { CHART_PROPS_REDESIGN } from 'site-modules/shared/constants/price-checker';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { getEspTrustDateText } from 'site-modules/shared/utils/esp';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { OdometerPaymentValue } from 'site-modules/shared/components/car-buying/odometer-payment-value/odometer-payment-value';
import { useIsClient } from 'site-modules/shared/hooks/use-is-client';
import { PricingChart } from './pricing-chart';

import './price-checker-promo-chart.scss';

function PriceCheckerPromoChartUI({
  tmvChartData = null,
  className = null,
  isMobile = false,
  columnWidth = null,
  columnPadding = null,
  animationBegin,
  idPrefix = '',
  hidePriceLabels = false,
  priceLabelClassName = 'heading-6 mb-0_25',
  priceClassName = 'heading-4',
  showEspTrustDate = true,
}) {
  const animate = useIsClient();

  const currentTmvChartData = animate && tmvChartData;

  const customPriceLabels = currentTmvChartData
    ? [
        {
          label: formatPriceString(currentTmvChartData.maxGreatPrice),
          index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.lowEnd,
        },
        {
          label: formatPriceString(currentTmvChartData.maxFairPrice),
          index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.highEnd,
          end: true,
        },
      ]
    : [
        { index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.lowEnd },
        { index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.highEnd, end: true },
      ];

  return (
    <div className={classnames('price-checker-promo-chart text-center w-100', className)}>
      <div className="chart-heading">
        {currentTmvChartData ? (
          <Fragment>
            {showEspTrustDate && (
              <div className="small fst-italic text-cool-gray-40 mb-0_5">{getEspTrustDateText()}</div>
            )}
            <span className={priceLabelClassName}>{currentTmvChartData.dealerPrice.cta}</span>
            {!!currentTmvChartData.dealerPrice.tooltip && (
              <PopoverItem className="text-primary-darker" screenReaderTooltipLabel="dealer price">
                {currentTmvChartData.dealerPrice.tooltip}
              </PopoverItem>
            )}
          </Fragment>
        ) : (
          <div className="heading-4">Check your price quote</div>
        )}
        {/* 'd-none' to have animation on first input */}
        <div
          className={classnames('user-price', priceClassName, {
            'd-none': !currentTmvChartData,
            'd-flex align-items-center justify-content-center': currentTmvChartData,
          })}
          style={{ color: get(currentTmvChartData, 'dealerPrice.color') }}
        >
          {get(currentTmvChartData, 'dealerPrice.label')} Price:{' '}
          <OdometerPaymentValue
            value={get(currentTmvChartData, 'maxGoodPrice')}
            className="ms-0_25 col-auto"
            valueClassName="align-top"
          />
        </div>
      </div>
      <PricingChart
        {...CHART_PROPS_REDESIGN.COMMON}
        columnWidth={columnWidth || CHART_PROPS_REDESIGN.COMMON.columnWidth}
        columnPadding={columnPadding || CHART_PROPS_REDESIGN.COMMON.columnPadding}
        dataPoints={
          currentTmvChartData
            ? CHART_PROPS_REDESIGN.DATA_POINTS.WITH_DATA
            : CHART_PROPS_REDESIGN.DATA_POINTS.WITHOUT_DATA
        }
        customPriceLabels={hidePriceLabels ? null : customPriceLabels}
        selectedPriceIndex={currentTmvChartData ? 6.8 : undefined}
        isMobile={isMobile}
        hasAnimation
        animationBegin={animationBegin}
        idPrefix={idPrefix}
        isSample
      />
    </div>
  );
}

PriceCheckerPromoChartUI.propTypes = {
  className: PropTypes.string,
  tmvChartData: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
  animationBegin: PropTypes.string,
  columnWidth: PropTypes.shape({
    desktop: PropTypes.number.isRequired,
    mobile: PropTypes.number.isRequired,
  }),
  columnPadding: PropTypes.number,
  priceLabelClassName: PropTypes.string,
  priceClassName: PropTypes.string,
  idPrefix: PropTypes.string,
  hidePriceLabels: PropTypes.bool,
  showEspTrustDate: PropTypes.bool,
};

const mapStateToProps = state => ({
  visitorLocation: get(state, 'visitor.location', {}),
});

export const PriceCheckerPromoChart = connect(mapStateToProps)(PriceCheckerPromoChartUI);
